import { trackEvent } from '@patrianna/analytics'
import { getDialogVisibilityByName, openDialog, replaceDialog } from '@patrianna/dialog-manager'
import { getQuickPurchaseSupportedTypeSelector } from '@patrianna-payments/shared-store/payments/store/selectors'
import type { OfferInfo } from '@patrianna-payments/shared-store/payments/types/openApiModels/OfferInfo'
import { convertOpenApiErrorResponseToGateway } from '@patrianna-payments/shared-store/payments/utils/convertOpenApiErrorResponseToGateway'
import type { useRouter } from 'next/navigation'

import ROUTES from 'config/routes.json'
import type { TypedThunk } from 'src/store/types'
import { sweepstakeEnabledSelector } from 'store/modules/appConfig/selectors'
import { getActiveModalCurrencyIdSelector } from 'store/modules/currencies/selectors'
import { getAllSCDiscountOffersSelector, getModalOffersSelector } from 'store/modules/shop/selectors'
import { getUserXpLevelSelector, isLoggedInSelector, shouldForceVerifyKYCSelector } from 'store/modules/user/selectors'
import type { SupportedCurrencies } from 'types/currencies'

import { openSnackbar } from '../snackbar/actions'

import { actions } from './slice'

export const {
  clearProducts,
  addAppliedOffers,
  setIsUserOffers,
  addOffersAction,
  setPurchaseLimits,
  setOffersLoaded,
  setActivatedGame,
  addLastPurchasedOffer,
  setOffersLastFetchedAt,
} = actions

export const getOffers =
  (cb?: () => void): TypedThunk =>
  (dispatch) => {
    dispatch(setOffersLastFetchedAt(Date.now()))

    return dispatch(getOffersRequest(cb))
  }

export const getOffersRequest =
  (cb?: () => void): TypedThunk =>
  (dispatch, getState, { payment }) => {
    payment
      .getOffers({})
      .then((body) => {
        const isLoggedIn = isLoggedInSelector(getState())
        dispatch(addOffersAction({ offers: body.offers as unknown as OfferInfo[] }))
        dispatch(addAppliedOffers({ appliedOffers: body.applied as unknown as OfferInfo[] }))
        dispatch(setIsUserOffers({ isUserOffers: isLoggedIn }))
        dispatch(addLastPurchasedOffer({ lastPurchasedOffer: body.lastPurchasedOffer as unknown as OfferInfo }))
        cb?.()

        if (body.limitAmount) {
          dispatch(
            setPurchaseLimits({
              limits: {
                limitAmount: body.limitAmount,
                limitAvailable: body.limitAvailable,
                limitEnd: body.limitEnd,
                limitPeriod: body.limitPeriod,
              },
            }),
          )
        }
      })
      .catch(convertOpenApiErrorResponseToGateway('GetOffersResponse'))
      .catch((error) => {
        if (error?.status?.errorText && error?.status?.errorCode !== 'err_timeout') {
          dispatch(
            openSnackbar({
              message: error.status.errorText,
            }),
          )
        }

        dispatch(setOffersLastFetchedAt(0))
      })
      .finally(() => {
        dispatch(setOffersLoaded(true))
      })
  }

export const openExtraSweepDialogIfPossible = (): TypedThunk => (dispatch, getState) => {
  const isForceShowKYC = shouldForceVerifyKYCSelector(getState())
  const bonusOffer = getModalOffersSelector(getState())
  const isSweepAvailable = sweepstakeEnabledSelector(getState())
  const isPaymentDialogVisible = getDialogVisibilityByName('PAYMENT_DIALOG')
  const isOrderConfirmationDialogVisible = getDialogVisibilityByName('ORDER_CONFIRMATION_MESSAGE')

  if (
    bonusOffer?.length &&
    isSweepAvailable &&
    !isPaymentDialogVisible &&
    !isOrderConfirmationDialogVisible &&
    !isForceShowKYC
  ) {
    replaceDialog({ dialogName: 'EXTRA_GOLD_DIALOG' })
  }
}

export const outOfMoneyHandler =
  (name: string, gameType: 'slots' | 'lottery', currency: SupportedCurrencies): TypedThunk =>
  (dispatch, getState) => {
    const bonusOffer = getAllSCDiscountOffersSelector(getState())
    if (bonusOffer && currency === 'GC') {
      replaceDialog({ dialogName: 'EXTRA_GOLD_DIALOG' })
    }

    switch (gameType) {
      case 'slots': {
        const userLevel = getUserXpLevelSelector(getState())

        trackEvent('spin_out_of_balance', { category: name, label: userLevel.level.toString() })
        break
      }
      case 'lottery': {
        const activeCurrency = getActiveModalCurrencyIdSelector(getState())
        const userLevel = getUserXpLevelSelector(getState())

        trackEvent(
          `lotto_insufficient_funds_${activeCurrency}`.toLowerCase() as
            | 'lotto_insufficient_funds_gc'
            | 'lotto_insufficient_funds_sc',
          {
            category: name,
            label: userLevel.level.toString(),
          },
        )
        break
      }
      default: {
        break
      }
    }
  }

export const openShopHandler =
  (
    isMobile: boolean,
    routerPush: ReturnType<typeof useRouter>['push'],
    fromUrl?: string,
    isQuickPurchaseFlow?: boolean,
  ): TypedThunk =>
  (dispatch, getState) => {
    const locationPathname = window.location.pathname

    const { isQuickPurchase, isQuickPurchaseGetCoins } = getQuickPurchaseSupportedTypeSelector(
      getState(),
      locationPathname,
    )

    if ((isQuickPurchase || isQuickPurchaseGetCoins) && isQuickPurchaseFlow) {
      return openDialog({ dialogName: 'QUICK_PURCHASE_DIALOG' })
    }

    trackEvent('clicked_on_shop_button')
    // open offer dialog after shop was opened

    const offersLastFetchedAt = getState().shop.offersLastFetchedAt
    const TTL = 2 * 60 * 1000
    const expired = Date.now() - offersLastFetchedAt > TTL

    if (expired) {
      dispatch(getOffers())
    }

    if (isMobile) {
      openDialog({ dialogName: 'SHOP_DIALOG' })
    } else {
      const from = fromUrl ? `?from=${encodeURIComponent(fromUrl)}` : ''
      routerPush(`${ROUTES.SHOP}${from}`)
    }
  }

// const IS_OFFER_PROCESSING = false
